import { Stack, Typography } from '@mui/material'
import type { TransactionBuilder } from '@orca-so/common-sdk'
import type { TransactionBuilder as LegacyTransactionBuilder } from '@renec-foundation/escrow-sdk/node_modules/@orca-so/common-sdk'
import { SendTransactionError, TransactionError } from '@solana/web3.js'
import type { OptionsObject } from 'notistack'
import { ReactNode, useCallback, useMemo } from 'react'

import { isChainSolana } from '@/constants/index'
import { toResultAsync } from '@/utils/helpers'
import { useConnection } from '@/wallet/adapter'

import useConfirmTransaction from './use-confirmation-transaction'
import useGaslessHandling from './use-gasless-handling'
import { useSettings } from './use-settings'
import useToastMessage from './use-toast-message'
import useTrans from './useTrans'

export type TxErrorNoticeContext =
  | 'common'
  | 'create_buy_offer'
  | 'create_sell_offer'
  | 'create_trade'
  | 'cancel_offer'
  | 'create_limit_order'
  | 'create_pool'
  | 'create_position'
  | 'swap'
  | 'make_asset_withdrawal'

export type TxHash = string

export type NoticeResultListener<R = unknown> =
  ((type: 'confirmation_error', txHash: TxHash, error: TransactionError) => R) &
  ((type: 'error', error: SendTransactionError) => R) &
  ((type: 'success', txHash: TxHash) => R)

type NoticeElement<P extends unknown[]> = ReactNode | ((...params: P) => ReactNode)

export interface TxErrorNoticeOptions {
  alwaysHideTxExplorerLink?: boolean
  confirmationErrorMessage?: NoticeElement<[TxHash, TransactionError]>
  confirmationErrorTitle?: NoticeElement<[TxHash, TransactionError]>
  context?: TxErrorNoticeContext
  errorMessage?: NoticeElement<[SendTransactionError]>
  errorTitle?: NoticeElement<[SendTransactionError]>
  gasless?: boolean
  onBeforeShowingNotice?: NoticeResultListener
  primaryButton?: ReactNode | NoticeResultListener<ReactNode>
  snackbarOptions?: OptionsObject
  successMessage?: NoticeElement<[TxHash]>
  successTitle?: NoticeElement<[TxHash]>
  throwOnError?: boolean
  titleAppendix?: ReactNode | NoticeResultListener<ReactNode>
  waitUntilConfirmed?: boolean
}

const KnownErrorTesters = {
  '0x1': [
    /Attempt to debit an account but found no record of a prior credit/,
    /custom program error: 0x1$/,
  ],
  'wallet_refused': [
    /^refuse_send_transaction$/, // Carax wallet mobile - cancel signing
    /^user denied transaction signature$/, // Carax wallet extension - cancel signing
    /^user denied sign transactions$/, // Carax wallet extension - cancel signing
    /^WalletSignTransactionError$/, // Carax wallet extension - refuse unlocking
    /^WalletSignTransactionError: Transaction rejected$/, // Solflare wallet extension
    /^WalletSignTransactionError: User rejected the request\.$/, // Phantom wallet extension
  ],
}

export const isKnownError = (error: Error) => {
  return Object.entries(KnownErrorTesters).find(([, testers]) =>
    testers.some((tester) => tester.test(error.message) || tester.test(String(error))),
  )?.[0] as keyof typeof KnownErrorTesters | undefined
}

const useSafelySendTxWithNotice = (
  globalContextOrOptions: TxErrorNoticeContext | TxErrorNoticeOptions = {},
) => {
  const globalOptions = useMemo(
    () =>
      typeof globalContextOrOptions === 'string'
        ? { context: globalContextOrOptions }
        : globalContextOrOptions,
    [globalContextOrOptions],
  )

  const { connection } = useConnection()
  const { confirmTransaction } = useConfirmTransaction()
  const { showMessage, showLoadingNotice } = useToastMessage()
  const trans = useTrans()
  const { data: settingsData } = useSettings()
  const gaslessEnabled = Boolean(settingsData?.is_gasless_mode_enabled)
  const sendTxWithGasless = useGaslessHandling()

  const safelySendTxWithNotice = useCallback(
    /**
     * @throws never, unless `throwOnError` is set to `true`.
     */
    async (
      tx: Promise<TxHash> | TransactionBuilder | LegacyTransactionBuilder,
      {
        alwaysHideTxExplorerLink = globalOptions.alwaysHideTxExplorerLink ?? false,
        confirmationErrorMessage = globalOptions.confirmationErrorMessage ??
          trans.common.tx_notice.unconfirmed_message,
        confirmationErrorTitle = globalOptions.confirmationErrorTitle ??
          globalOptions.errorTitle ??
          trans.common.tx_notice.error_title,
        context = globalOptions.context ?? 'common',
        errorMessage = globalOptions.errorMessage ?? trans.common.tx_notice.error_message,
        errorTitle = globalOptions.errorTitle ?? trans.common.tx_notice.error_title,
        gasless = globalOptions.gasless ?? gaslessEnabled,
        onBeforeShowingNotice = globalOptions.onBeforeShowingNotice ?? (() => true),
        primaryButton = globalOptions.primaryButton,
        snackbarOptions = globalOptions.snackbarOptions,
        successMessage = globalOptions.successMessage ?? trans.common.tx_notice.success_message,
        successTitle = globalOptions.successTitle ?? trans.common.tx_notice.success_title,
        throwOnError = globalOptions.throwOnError ?? false,
        titleAppendix = globalOptions.titleAppendix,
        waitUntilConfirmed = globalOptions.waitUntilConfirmed ?? true,
      }: TxErrorNoticeOptions = {},
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const noticeElement = (e: NoticeElement<any[]>, ...args: any[]) =>
        typeof e === 'function' ? e(...args) : e

      const [error, signature] = await toResultAsync<SendTransactionError, string>(
        'then' in tx ? tx : ((gasless && !isChainSolana()) ? sendTxWithGasless(tx) : tx.buildAndExecute()),
      )

      if (error) {
        const errorKey = isKnownError(error) ?? 'unknown'

        const errorTroubleshootingByContext = trans.common.tx_notice.errors[errorKey]
        const troubleshooting = [...(errorTroubleshootingByContext?.common ?? [])]
        if (context !== 'common' && errorTroubleshootingByContext?.[context]) {
          troubleshooting.splice(0, 0, ...errorTroubleshootingByContext[context])
        }

        // eslint-disable-next-line no-console
        console.error(`${context} send transaction error`, error, error.logs)

        if (onBeforeShowingNotice('error', error) !== false) {
          showMessage(
            {
              error,
              message: (
                <Stack spacing={2}>
                  <Typography className="text-sm">{noticeElement(errorMessage, error)}</Typography>
                  {Boolean(troubleshooting.length) && (
                    <Stack spacing={1}>
                      <Typography className="text-xs font-bold">
                        {trans.common.tx_notice.what_should_i_do_now}
                      </Typography>
                      <Stack spacing={0.5}>
                        {troubleshooting.map((solution, index) => (
                          <Typography component="li" className="text-sm" key={index}>
                            {solution}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              ),
              primaryButton: noticeElement(primaryButton, 'error', error),
              title: noticeElement(errorTitle, error),
              titleAppendix: noticeElement(titleAppendix, 'error', error),
              type: 'error',
            },
            snackbarOptions,
          )
        }

        if (throwOnError) throw error

        return
      }

      if (waitUntilConfirmed) {
        const latestBlockhash = await connection.getLatestBlockhash()

        const closeLoadingNotice = showLoadingNotice({
          lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
          signature,
        })

        const [
          isTxConfirmed,
          {
            value: { err: confirmationError },
          },
        ] = await confirmTransaction(signature, latestBlockhash)

        closeLoadingNotice()

        if (!isTxConfirmed && confirmationError) {
          // eslint-disable-next-line no-console
          console.error(`${context} confirm transaction error`, confirmationError)

          if (onBeforeShowingNotice('confirmation_error', signature, confirmationError) !== false) {
            showMessage(
              {
                message: noticeElement(confirmationErrorMessage, signature, error),
                primaryButton: noticeElement(primaryButton, 'confirmation_error', signature, error),
                title: noticeElement(confirmationErrorTitle, signature, error),
                titleAppendix: noticeElement(titleAppendix, 'confirmation_error', signature, error),
                type: 'error',
                txid: alwaysHideTxExplorerLink ? undefined : signature,
              },
              snackbarOptions,
            )
          }

          if (throwOnError) throw confirmationError

          return
        }
      }

      // eslint-disable-next-line no-console
      console.log(`${context} transaction success`, signature)

      if (onBeforeShowingNotice('success', signature) !== false) {
        showMessage(
          {
            message: noticeElement(successMessage, signature),
            primaryButton: noticeElement(primaryButton, 'success', signature),
            title: noticeElement(successTitle, signature),
            titleAppendix: noticeElement(titleAppendix, 'success', signature),
            type: 'success',
            txid: alwaysHideTxExplorerLink ? undefined : signature,
          },
          snackbarOptions,
        )
      }

      return signature
    },
    [
      confirmTransaction,
      connection,
      gaslessEnabled,
      globalOptions,
      sendTxWithGasless,
      showLoadingNotice,
      showMessage,
      trans,
    ],
  )

  return { safelySendTxWithNotice }
}

export default useSafelySendTxWithNotice
